<template>
    <component
        :is="component"
        :template="template"
        :criterion="criterion"
        :custom-properties="customProperties"
        :load-criterion-message="loadCriterionMessage"
        :show-validation-errors="showValidationErrors"
        @input="$emit('input', $event)"
        @reference-accessed="$emit('reference-accessed', $event)"
        @controller-value-update="$emit('controller-value-update', $event)"
        @update:visible-schemas="$emit('update:visible-schemas', $event)"
    />
</template>

<script lang="ts">
    import { Component, defineComponent, PropType } from 'vue';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import EidCriterion
        from '@/modules/decisioning-criteria/components/EidCriterion/EidCriterion.vue';
    import PublicProductsLiabilityCriterion
        from '@/modules/decisioning-criteria/components/PublicProductsLiabilityCriterion/PublicProductsLiabilityCriterion.vue';
    import RecollectionIntervalCriterion
        from '@/modules/decisioning-criteria/components/RecollectionIntervalCriterion/RecollectionIntervalCriterion.vue';
    import RiskProfileCriterion from '@/modules/decisioning-criteria/components/RiskProfileCriterion/RiskProfileCriterion.vue';
    import { LoadCriterionMessage } from '@/modules/decisioning-criteria/models/CriterionSettingLoadMessageFunc.model';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';

    import {
        isNewJsonSchemaForm,
        isPublicOrProductsLiability,
        isRecollectionIntervalCriterion,
    } from './helpers';

    interface CriterionCompCondition {
        conditionFunction: Function;
        args: any[];
        component: Component;
    }

    export default defineComponent({
        name: 'DynamicCriterionSetting',
        components: {
            PublicProductsLiabilityCriterion,
            RecollectionIntervalCriterion,
            RiskProfileCriterion,
            EidCriterion,
        },
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            loadCriterionMessage: {
                type: Function as PropType<LoadCriterionMessage>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            showValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'controller-value-update', 'input', 'reference-accessed', 'update:visible-schemas' ],
        computed: {
            component(): Component {
                return this.getComponent(this.criterionCompConditions);
            },
            criterionCompConditions(): CriterionCompCondition[] {
                return [
                    this.getCondition(isNewJsonSchemaForm, EidCriterion),
                    this.getCondition(isPublicOrProductsLiability, PublicProductsLiabilityCriterion),
                    this.getCondition(isRecollectionIntervalCriterion, RecollectionIntervalCriterion),
                ];
            },
        },
        methods: {
            getCondition(
                conditionFunction: Function,
                component: Component,
                optionalArgs?: any[],
            ): CriterionCompCondition {
                const args = optionalArgs || [ this.template ];
                return { conditionFunction, args, component };
            },
            getComponent(criterionConditions: CriterionCompCondition[]): Component {
                for (const { conditionFunction, args, component } of criterionConditions) {
                    if (conditionFunction(...args)) {
                        return component;
                    }
                }
                return RiskProfileCriterion;
            },
        },
    });
</script>
